// F13 (ADR-0146) — el vocabulario del centro de notificaciones del
// propietario. Vive en el engine, no en una receta: es el canal del
// propietario, igual para toda vertical con una contraparte EXTERNA
// (`assetPartyBinding(recipe).audience === "external"`; hoy, postventa).
// Facility nunca escribe filas: su asset_party es personal.
//
// Las CATEGORÍAS son las claves del handoff (`NCAT` de
// `propietario_canal_unico.js`). Seis declaradas; tienen escritor `obs` y
// `consulta` (corte 3), `entrega` (F9, corte 8) y, desde #0748 (ADR-0152),
// `aviso` y `mant` (los avisos y las guías de mantención por proyecto).
// `doc` espera su backend (F11) y la PWA no muestra su chip hasta entonces.
// `enc` (ADR-0153, #0750): las encuestas del propietario (NPS a los 2 y 9
// meses de la entrega y «¿Cómo fue la visita técnica?»).
//
// Los TIPOS (`kind`) son el hecho puntual que generó el aviso. La fila
// sólo guarda ids: título, cuerpo y acción se arman al leer, en core-api.
import { z } from "zod";
export const OWNER_NOTIFICATION_CATEGORIES = [
    "obs",
    "consulta",
    "aviso",
    "mant",
    "doc",
    "entrega",
    "enc",
];
export const ownerNotificationCategorySchema = z.enum(OWNER_NOTIFICATION_CATEGORIES);
/** Las categorías que hoy tienen escritor: las únicas que la PWA ofrece
 *  como filtro (regla de ocultar lo que no se puede usar). */
export const ACTIVE_OWNER_NOTIFICATION_CATEGORIES = [
    "obs",
    "consulta",
    // ADR-0152 (#0748) — avisos y guías de mantención por proyecto.
    "aviso",
    "mant",
    "entrega",
    // ADR-0153 (#0750) — encuestas del propietario.
    "enc",
];
export const OWNER_NOTIFICATION_KINDS = [
    // El contratista la marcó resuelta: espera la revisión del propietario.
    "observation_ready_for_review",
    // Primera asignación (corte 7: reemplaza a «aceptada» si aceptó de paso).
    "observation_assigned",
    "observation_accepted",
    "observation_rejected",
    "observation_appeal_resolved",
    // ADR-0142: los recordatorios del cierre por falta de respuesta (el
    // último avisa que se cerrará) y el cierre mismo.
    "observation_closure_reminder",
    "observation_closure_last_reminder",
    "observation_auto_closed",
    // ADR-0145: la inmobiliaria respondió una consulta (sin el texto).
    "inquiry_reply",
    // F8 (corte 6): la inmobiliaria agendó o cambió la visita del contratista.
    "observation_visit_scheduled",
    // F9 (corte 8): la inmobiliaria fijó o cambió la fecha de entrega de la
    // unidad. Apunta al asset, no a una observación.
    "delivery_scheduled",
    // ADR-0152 (#0748): la inmobiliaria publicó un aviso o una guía de
    // mantención para todo el proyecto. Un tipo por categoría: la forma
    // (tipo → categoría) es fija, como la de los demás.
    "announcement_published",
    "maintenance_guide_published",
    // ADR-0153 (#0750): una encuesta del propietario. Dos tipos, no tres:
    // la ola del NPS (2 o 9 meses) vive en `owner_surveys.kind`.
    "survey_nps",
    "survey_visit",
    // ADR-0161 (#0768): el contratista propuso la visita (por confirmar) y
    // la visita se confirmó sola porque el propietario no respondió.
    "observation_visit_proposed",
    "observation_visit_auto_confirmed",
    // ADR-0163 (#0770): el jefe convirtió una consulta del propietario en
    // observación. Apunta a la observación nueva; la consulta queda cerrada.
    "inquiry_converted",
];
export const ownerNotificationKindSchema = z.enum(OWNER_NOTIFICATION_KINDS);
/** A qué entidad apunta la fila: de ahí sale el deep link. */
export const OWNER_NOTIFICATION_ENTITY_TYPES = [
    "finding",
    "owner_inquiry",
    "asset",
    "announcement",
    "owner_survey",
];
export const ownerNotificationEntityTypeSchema = z.enum(OWNER_NOTIFICATION_ENTITY_TYPES);
export const OWNER_NOTIFICATION_CATEGORY_BY_KIND = {
    observation_ready_for_review: "obs",
    observation_assigned: "obs",
    observation_accepted: "obs",
    observation_rejected: "obs",
    observation_appeal_resolved: "obs",
    observation_closure_reminder: "obs",
    observation_closure_last_reminder: "obs",
    observation_auto_closed: "obs",
    inquiry_reply: "consulta",
    observation_visit_scheduled: "obs",
    delivery_scheduled: "entrega",
    announcement_published: "aviso",
    maintenance_guide_published: "mant",
    survey_nps: "enc",
    survey_visit: "enc",
    observation_visit_proposed: "obs",
    observation_visit_auto_confirmed: "obs",
    inquiry_converted: "obs",
};
export const OWNER_NOTIFICATION_ENTITY_BY_KIND = {
    observation_ready_for_review: "finding",
    observation_assigned: "finding",
    observation_accepted: "finding",
    observation_rejected: "finding",
    observation_appeal_resolved: "finding",
    observation_closure_reminder: "finding",
    observation_closure_last_reminder: "finding",
    observation_auto_closed: "finding",
    inquiry_reply: "owner_inquiry",
    observation_visit_scheduled: "finding",
    delivery_scheduled: "asset",
    announcement_published: "announcement",
    maintenance_guide_published: "announcement",
    survey_nps: "owner_survey",
    survey_visit: "owner_survey",
    observation_visit_proposed: "finding",
    observation_visit_auto_confirmed: "finding",
    inquiry_converted: "finding",
};
/**
 * La clave de deduplicación de un aviso nacido de un hito del hilo
 * (`finding_events.id`): un hito es un hecho, y un reintento que no
 * crea hito (los guardas de las rutas) no crea aviso.
 */
export function findingEventNotificationKey(kind, findingEventId) {
    return `${kind}/${findingEventId}`;
}
/**
 * La clave de un aviso del cierre por falta de respuesta: la MISMA tupla
 * (observación, ciclo, aviso) que `observation_notices`, sin la
 * generación — un reintento del correo (otra generación) no repite el
 * aviso en la app.
 */
export function closureNoticeNotificationKey(findingId, cycle, noticeKind) {
    return `observation-notice/${findingId}/${cycle}/${noticeKind}`;
}
/** La clave de «respondimos tu consulta»: una por mensaje del staff. */
export function inquiryReplyNotificationKey(messageId) {
    return `inquiry-reply/${messageId}`;
}
/** ADR-0163 — la clave de «tu consulta ahora es una observación»: una por
 *  conversión (una consulta se convierte a lo más una vez). */
export function inquiryConvertedNotificationKey(conversionId) {
    return `inquiry-converted/${conversionId}`;
}
/**
 * La clave del aviso de entrega (F9): la unidad, el instante fijado y el
 * cambio que lo fijó (el id de su `audit_events`). La misma fecha no se
 * vuelve a avisar porque la ruta no escribe nada si no cambió; una fecha
 * nueva es otro cambio, y volver a una fecha anterior también (con sólo
 * el instante, la tercera vuelta A → B → A chocaría con el primer aviso y
 * el propietario se quedaría creyendo B).
 */
export function deliveryScheduledNotificationKey(assetId, isoInstant, changeId) {
    return `entrega/${assetId}/${isoInstant}/${changeId}`;
}
// ─── avisos por proyecto (ADR-0152, #0748) ───────────────────────────
/** Las dos clases de aviso que publica la inmobiliaria: el aviso del
 *  edificio y la guía de mantención. Son también su categoría. */
export const ANNOUNCEMENT_CATEGORIES = [
    "aviso",
    "mant",
];
export const announcementCategorySchema = z.enum(ANNOUNCEMENT_CATEGORIES);
/** El tipo de la fila de la campana de cada clase. */
export const ANNOUNCEMENT_NOTIFICATION_KIND = {
    aviso: "announcement_published",
    mant: "maintenance_guide_published",
};
/**
 * La clave de la fila de la campana de un aviso: una por (aviso, unidad).
 * Un propietario con dos unidades en el proyecto ve el aviso en cada una
 * (el filtro por propiedad funciona), y si una unidad cambia de dueño el
 * anterior deja de verlo — la misma regla que el resto de la campana.
 */
export function announcementNotificationKey(announcementId, assetId) {
    return `announcement/${announcementId}/${assetId}`;
}
/**
 * La clave de la fila de la campana de una encuesta (ADR-0153): una por
 * encuesta. La fila de `owner_surveys` ya es «una por hito», así que la
 * clave sólo repite su id.
 */
export function ownerSurveyNotificationKey(surveyId) {
    return `encuesta/${surveyId}`;
}
// ─── preferencias (ADR-0147, fase 2 corte 4, #0742) ──────────────────
/** Los canales por los que el propietario recibe un aviso. */
export const OWNER_NOTIFICATION_CHANNELS = ["email", "app"];
export const ownerNotificationChannelSchema = z.enum(OWNER_NOTIFICATION_CHANNELS);
/**
 * Avisos que sólo salen por correo (no dejan fila en la campana): el acuse
 * de una consulta, que confirma un acto que el propietario acaba de hacer.
 * Entran al vocabulario de preferencias porque un correo así puede estar
 * bloqueado o no; nunca a `notifications`.
 */
export const OWNER_EMAIL_ONLY_NOTICES = ["inquiry_receipt"];
export const OWNER_NOTICE_CATEGORY = {
    ...OWNER_NOTIFICATION_CATEGORY_BY_KIND,
    inquiry_receipt: "consulta",
};
export const OWNER_NOTICE_LOCK = {
    observation_ready_for_review: "when_closure_active",
    observation_assigned: "never",
    observation_accepted: "never",
    observation_rejected: "always",
    observation_appeal_resolved: "always",
    observation_closure_reminder: "always",
    observation_closure_last_reminder: "always",
    observation_auto_closed: "always",
    inquiry_reply: "never",
    inquiry_receipt: "always",
    // Opcional, como «asignada»: si el propietario la apaga, la fecha sigue
    // en la franja de estado de su observación.
    observation_visit_scheduled: "never",
    // Opcional (F9): si el propietario la apaga, la fecha sigue en la home
    // de su unidad («Tu entrega»).
    delivery_scheduled: "never",
    // ADR-0152: opcionales (decisión del founder, 2026-10-16). Apagar «En la
    // app» los oculta; apagar «Correo» deja de mandarlos por correo.
    announcement_published: "never",
    maintenance_guide_published: "never",
    // ADR-0153: responder es opcional, así que las encuestas se pueden
    // silenciar en los dos canales.
    survey_nps: "never",
    survey_visit: "never",
    // ADR-0161: el aviso de la propuesta abre el plazo de la confirmación
    // automática (decisión 2 del founder): no se puede apagar. Que su
    // silencio confirmó una entrada a su domicilio tampoco: es sólo campana
    // (sin correo), así que su motivo lo dice («Solo en la app») y no queda
    // bajo el interruptor «Correo».
    observation_visit_proposed: "always",
    observation_visit_auto_confirmed: "always",
    // ADR-0163: no se apaga — la consulta se cerró y desde ese día el
    // propietario la sigue en la observación; necesita saber dónde quedó.
    inquiry_converted: "always",
};
/** ¿Este aviso sale siempre, diga lo que diga la preferencia? */
export function isOwnerNoticeLocked(kind, ctx) {
    const lock = OWNER_NOTICE_LOCK[kind];
    return lock === "always" || (lock === "when_closure_active" && ctx.closureActive);
}
/** Los tipos bloqueados de una categoría, en el orden del vocabulario. */
export function lockedOwnerNoticeKinds(category, ctx) {
    return Object.keys(OWNER_NOTICE_LOCK).filter((k) => OWNER_NOTICE_CATEGORY[k] === category && isOwnerNoticeLocked(k, ctx));
}
// ─── avisos en el teléfono (ADR-0162, #0769) ──────────────────────────
/**
 * Los tipos de la campana que NO suenan en el teléfono. `survey_visit`
 * nace del toque del propio propietario («conforme»): su destinatario es
 * quien acaba de actuar (la regla de «nunca se avisa a sí mismo»).
 */
export const OWNER_PUSH_EXCLUDED_KINDS = [
    "survey_visit",
];
/**
 * Los tipos que suenan en el teléfono. Se calculan por EXCLUSIÓN: un tipo
 * nuevo de la campana suena solo, con el texto de su categoría (el texto
 * de la pantalla bloqueada va por categoría, nunca por tipo).
 */
export const OWNER_PUSH_KINDS = OWNER_NOTIFICATION_KINDS.filter((k) => !OWNER_PUSH_EXCLUDED_KINDS.includes(k));
/** ¿Este tipo de la campana suena en el teléfono? */
export function isOwnerPushKind(kind) {
    return OWNER_PUSH_KINDS.includes(kind);
}
//# sourceMappingURL=owner-notifications.js.map